// src/pages/ProposalDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserCircleIcon,
  CheckIcon,
  CalendarIcon,
  WalletIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import LoadingSpinner from '../components/LoadingSpinner';
import VoterList from '../components/VoterList';
import Comments from '../components/Comments';
import DrawingComponent from '../components/DrawingComponent';

const ProposalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { account, isOasysNetwork, connectWallet, switchToOasysNetwork, signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [proposal, setProposal] = useState(null);
  const [prevProposal, setPrevProposal] = useState(null);
  const [nextProposal, setNextProposal] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [activeTab, setActiveTab] = useState('results');
  // const [copySuccess, setCopySuccess] = useState(false);

  // 状態変数を追加
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 円グラフの色設定
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];

  // 提案情報を取得
  useEffect(() => {
    const fetchProposalData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 提案情報を取得
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/proposals/${id}`);
        setProposal(response.data);

        // 前後の提案を取得
        const allProposalsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/proposals`
        );
        const allProposals = allProposalsResponse.data;

        // 作成日時でソート
        const sortedProposals = [...allProposals].sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt); // 新しい順
        });

        // 現在の提案のインデックスを特定
        const currentIndex = sortedProposals.findIndex(p => p.id.toString() === id);

        if (currentIndex > 0) {
          setNextProposal(sortedProposals[currentIndex - 1]);
        } else {
          setNextProposal(null);
        }

        if (currentIndex < sortedProposals.length - 1) {
          setPrevProposal(sortedProposals[currentIndex + 1]);
        } else {
          setPrevProposal(null);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('提案取得エラー:', error);
        setError('提案情報の取得に失敗しました');
        setIsLoading(false);
      }
    };

    if (id) {
      fetchProposalData();
    }
  }, [id]);

  // アカウントとプロポーザルが変わったときに選択肢を更新
  useEffect(() => {
    if (proposal && account) {
      // 既に投票済みかチェック
      const existingVote = proposal.votes?.find(
        vote => vote.voterAddress.toLowerCase() === account.toLowerCase()
      );

      if (existingVote) {
        setSelectedChoice(existingVote.choiceId);
      } else {
        setSelectedChoice(null);
      }
    } else {
      setSelectedChoice(null);
    }
  }, [proposal, account]);

  // 投票ハンドラー
  const handleVote = async () => {
    if (!account) {
      showNotification('ウォレットを接続してください', 'error');
      return;
    }

    if (!isOasysNetwork) {
      showNotification('OASYSネットワークに切り替えてください', 'error');
      return;
    }

    if (!selectedChoice) {
      showNotification('選択肢を選んでください', 'error');
      return;
    }

    try {
      setIsVoting(true);

      // 署名メッセージの作成
      const message = `提案に投票します\n提案ID: ${
        proposal.id
      }\n選択肢ID: ${selectedChoice}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 投票データの準備
      const voteData = {
        proposalId: proposal.id,
        choiceId: selectedChoice,
        signature,
        message,
      };

      // APIリクエスト
      await axios.post(`${process.env.REACT_APP_API_URL}/api/votes`, voteData);

      // 提案データを更新
      const updatedProposal = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/proposals/${id}`
      );
      setProposal(updatedProposal.data);

      showNotification('投票が完了しました', 'success');
    } catch (error) {
      console.error('投票エラー:', error);
      showNotification(error.response?.data?.error || '投票に失敗しました', 'error');
    } finally {
      setIsVoting(false);
    }
  };

  // 提案を削除する機能
  const handleDeleteProposal = async () => {
    try {
      setIsDeleting(true);

      // 署名メッセージの作成
      const message = `提案を削除します\n提案ID: ${proposal.id}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 削除リクエスト
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/proposals/${proposal.id}`, {
        data: {
          signature,
          message,
        },
      });

      showNotification('提案が削除されました', 'success');
      navigate('/proposals');
    } catch (error) {
      console.error('提案削除エラー:', error);
      showNotification(error.response?.data?.error || '提案の削除に失敗しました', 'error');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // 提案が編集可能かどうかチェック
  const isProposalEditable = () => {
    if (!proposal || !account) return false;

    // 作成者のみ編集可能
    const isCreator = proposal.creatorAddress.toLowerCase() === account.toLowerCase();

    // 準備中（投票開始前）の提案のみ編集可能
    const now = new Date();
    const startTime = new Date(proposal.startTime);
    const isPending = now < startTime;

    return isCreator && isPending;
  };

  // ウォレット接続ハンドラー
  const handleConnect = async () => {
    if (!account) {
      await connectWallet();
    } else if (!isOasysNetwork) {
      await switchToOasysNetwork();
    }
  };

  // // アドレスをクリップボードにコピー
  // const copyToClipboard = text => {
  //   navigator.clipboard.writeText(text).then(
  //     () => {
  //       setCopySuccess(true);
  //       setTimeout(() => setCopySuccess(false), 2000);
  //     },
  //     err => {
  //       console.error('クリップボードへのコピーに失敗しました:', err);
  //     }
  //   );
  // };

  // 投票集計結果を計算
  const calculateVoteResults = () => {
    if (!proposal || !proposal.votes) return [];

    const results = proposal.choices.map(choice => {
      const choiceVotes = proposal.votes.filter(vote => vote.choiceId === choice.id);

      let count = choiceVotes.length;
      let weight = 0;

      // トークン加重投票の場合は重みを計算
      if (proposal.votingMethod === 'TOKEN_WEIGHTED') {
        weight = choiceVotes.reduce((sum, vote) => {
          return sum + parseInt(vote.weight || '1');
        }, 0);
      } else {
        weight = count;
      }

      return {
        id: choice.id,
        text: choice.text,
        count,
        weight,
      };
    });

    // 投票数でソート
    return results.sort((a, b) => b.weight - a.weight);
  };

  // 合計投票重み
  const getTotalWeight = () => {
    if (!proposal || !proposal.votes) return 0;

    if (proposal.votingMethod === 'TOKEN_WEIGHTED') {
      return proposal.votes.reduce((sum, vote) => sum + parseInt(vote.weight || '1'), 0);
    } else {
      return proposal.votes.length;
    }
  };

  // 投票期間の状態を取得
  const getVotingPeriodStatus = () => {
    if (!proposal) return { status: 'unknown' };

    const now = new Date();
    const startTime = new Date(proposal.startTime);
    const endTime = new Date(proposal.endTime);

    if (now < startTime) {
      const diff = startTime - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      return {
        status: 'pending',
        text: '投票開始まで',
        timeRemaining: `${hours}時間${mins}分`,
      };
    } else if (now > endTime) {
      return {
        status: 'closed',
        text: '投票終了',
      };
    } else {
      const diff = endTime - now;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      return {
        status: 'active',
        text: '投票終了まで',
        timeRemaining: days > 0 ? `${days}日${hours}時間` : `${hours}時間${mins}分`,
      };
    }
  };

  // アドレスを短縮
  const shortenAddress = address => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const exportAllVotersCSV = () => {
    const headers = 'Number,Address,Choice,VotingPower,VotedAt\n';
    const rows = proposal.votes
      .map((vote, index) => {
        return `${index + 1},${vote.voterAddress},"${vote.choice?.text || 'Unknown'}",${
          vote.weight
        },${new Date(vote.createdAt).toLocaleString()}`;
      })
      .join('\n');

    const csvData = headers + rows;
    downloadCSV(csvData, `${proposal.title}_all_voters.csv`);
  };

  // Helper function to download CSV data
  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">
            エラーが発生しました
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">{error}</p>
          <Link
            to="/proposals"
            className="mt-4 inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            提案一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold dark:text-white">提案が見つかりません</h1>
          <Link
            to="/proposals"
            className="mt-4 inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            提案一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  const voteResults = calculateVoteResults();
  const totalWeight = getTotalWeight();
  const votingPeriodStatus = getVotingPeriodStatus();

  return (
    <div className="max-w-6xl mx-auto">
      {/* ヘッダー部分 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <Link
              to="/proposals"
              className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              提案一覧に戻る
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{proposal.title}</h1>
          </div>

          <div className="flex space-x-2">
            {/* 編集・削除ボタン - 提案が準備中の場合のみ表示 */}
            {isProposalEditable() && (
              <div className="flex space-x-2 mr-4">
                <Link
                  to={`/proposals/edit/${proposal.id}`}
                  className="px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  編集
                </Link>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-3 py-1.5 bg-red-600 dark:bg-red-500 text-white rounded hover:bg-red-700 dark:hover:bg-red-600 flex items-center"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  削除
                </button>
              </div>
            )}

            {/* 前後ナビゲーション */}
            <Link
              to={prevProposal ? `/proposals/${prevProposal.id}` : '#'}
              className={`px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center ${
                !prevProposal ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
              }`}
              title={prevProposal ? `前の提案: ${prevProposal.title}` : '前の提案はありません'}
              onClick={e => !prevProposal && e.preventDefault()}
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              前へ
            </Link>

            <Link
              to={nextProposal ? `/proposals/${nextProposal.id}` : '#'}
              className={`px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center ${
                !nextProposal ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
              }`}
              title={nextProposal ? `次の提案: ${nextProposal.title}` : '次の提案はありません'}
              onClick={e => !nextProposal && e.preventDefault()}
            >
              次へ
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {/* 期間 - 提案者の上に表示 */}
            <div className="flex items-center mb-3">
              <CalendarIcon className="h-5 w-5 text-gray-400 mr-1" />
              <span className="text-gray-500 mr-2">期間:</span>
              <span>
                {format(new Date(proposal.startTime), 'yyyy/MM/dd HH:mm', { locale: ja })}
                {' - '}
                {format(new Date(proposal.endTime), 'yyyy/MM/dd HH:mm', { locale: ja })}
              </span>
            </div>

            <div className="flex items-center">
              <UserCircleIcon className="h-5 w-5 text-gray-400 mr-1" />
              <span className="text-gray-500 mr-2">提案者:</span>
              <Link
                to={`/user/${proposal.creatorAddress}`}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {shortenAddress(proposal.creatorAddress)}
              </Link>

              {/* 投票方式を提案者の右側に表示 */}
              <span className="ml-4 text-gray-500 mr-2">投票方式:</span>
              <span>
                {proposal.votingMethod === 'ONE_ADDRESS_ONE_VOTE'
                  ? '1アドレス1票'
                  : 'トークン量比例'}
                {proposal.includeUnclaimed && ' (未Claim含む)'}
              </span>
            </div>

            <div className="flex items-center mt-2">
              <svg
                className="h-5 w-5 text-gray-400 mr-1"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 16V8M15 12H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-gray-500 mr-2">投票権トークン:</span>
              <Link
                to={`/tokens/${proposal.token?.grantToken}`}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {proposal.token?.name} ({proposal.token?.symbol})
              </Link>

              <span className="ml-4 text-gray-500 mr-2">トークンペア:</span>
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-sm">
                {proposal.token.pairType}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* 左側：説明と投票フォーム */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">提案内容</h2>
                <div className="prose max-w-none dark:prose-invert">
                  <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">
                    {proposal.description}
                  </p>
                </div>
              </div>
            </div>

            {/* 投票フォーム */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">投票</h2>

                {votingPeriodStatus.status === 'pending' ? (
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-md p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          投票はまだ開始されていません
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                          <p>投票開始までお待ちください。</p>
                          <p className="mt-1">
                            <span className="font-medium">{votingPeriodStatus.timeRemaining}</span>{' '}
                            後に開始されます。
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : votingPeriodStatus.status === 'closed' ? (
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-gray-400 dark:text-gray-300"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          投票は終了しました
                        </h3>
                      </div>
                    </div>
                  </div>
                ) : !account ? (
                  <div className="text-center py-4 flex flex-col items-center justify-center">
                    <p className="mb-4">投票するにはウォレットを接続してください</p>
                    <button
                      onClick={handleConnect}
                      className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <WalletIcon className="h-5 w-5 mr-1" />
                      <span>ウォレット接続</span>
                    </button>
                  </div>
                ) : !isOasysNetwork ? (
                  <div className="text-center py-4 flex flex-col items-center justify-center">
                    <p className="mb-4">投票するにはOASYSネットワークに切り替えてください</p>
                    <button
                      onClick={switchToOasysNetwork}
                      className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <svg
                        className="h-5 w-5 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      OASYSネットワークに切替
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="space-y-3">
                      {proposal.choices.map(choice => (
                        <div key={choice.id} className="relative">
                          <input
                            type="radio"
                            id={`choice-${choice.id}`}
                            name="voting-choice"
                            value={choice.id}
                            checked={selectedChoice === choice.id}
                            onChange={() => setSelectedChoice(choice.id)}
                            className="hidden peer"
                          />
                          <label
                            htmlFor={`choice-${choice.id}`}
                            className="flex p-4 border dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 peer-checked:border-blue-500 peer-checked:ring-1 peer-checked:ring-blue-500"
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <div className="w-5 h-5 mr-3 border dark:border-gray-500 rounded-full flex items-center justify-center peer-checked:border-blue-500">
                                  {selectedChoice === choice.id && (
                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                  )}
                                </div>
                                <span className="text-gray-900 dark:text-white">{choice.text}</span>
                              </div>
                              {selectedChoice === choice.id && (
                                <CheckIcon className="h-5 w-5 text-blue-500" />
                              )}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6">
                      <button
                        onClick={handleVote}
                        disabled={!selectedChoice || isVoting}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 disabled:cursor-not-allowed"
                      >
                        {isVoting ? (
                          <span className="flex items-center justify-center">
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">投票中...</span>
                          </span>
                        ) : (
                          '投票する'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 抽選フォーム */}
            {votingPeriodStatus.status === 'closed' && (
              // <div className="p-6">
              <DrawingComponent proposal={proposal} />
              // </div>
            )}
          </div>

          {/* 右側：投票結果、投票者一覧、コメント */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
              {/* タブナビゲーション */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveTab('results')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
                    activeTab === 'results'
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  結果
                </button>

                <button
                  onClick={() => setActiveTab('voters')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
                    activeTab === 'voters'
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  投票者
                </button>

                <button
                  onClick={() => setActiveTab('comments')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm focus:outline-none ${
                    activeTab === 'comments'
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  コメント
                </button>
              </div>

              {/* タブコンテンツ */}
              <div className="p-6">
                {/* 結果タブ */}
                {activeTab === 'results' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">投票結果</h2>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {votingPeriodStatus.status === 'active' ? (
                          <span className="flex items-center text-green-600 dark:text-green-400">
                            <span className="inline-block w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full mr-1"></span>
                            {votingPeriodStatus.text} {votingPeriodStatus.timeRemaining}
                          </span>
                        ) : votingPeriodStatus.status === 'pending' ? (
                          <span className="flex items-center text-yellow-600 dark:text-yellow-400">
                            <span className="inline-block w-2 h-2 bg-yellow-600 dark:bg-yellow-400 rounded-full mr-1"></span>
                            {votingPeriodStatus.text} {votingPeriodStatus.timeRemaining}
                          </span>
                        ) : (
                          <span className="flex items-center text-gray-600 dark:text-gray-400">
                            <span className="inline-block w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full mr-1"></span>
                            {votingPeriodStatus.text}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        総投票数: {proposal.votes?.length || 0} / 総投票力: {totalWeight}
                      </div>

                      {voteResults.length > 0 ? (
                        <div>
                          <div className="mb-4">
                            <ResponsiveContainer width="100%" height={200}>
                              <PieChart>
                                <Pie
                                  data={voteResults}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="weight"
                                  nameKey="text"
                                >
                                  {voteResults.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip formatter={value => `${value}票`} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>

                          <div className="space-y-3">
                            {voteResults.map((result, index) => {
                              const percent =
                                totalWeight > 0 ? (result.weight / totalWeight) * 100 : 0;

                              return (
                                <div key={result.id} className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span className="font-medium text-gray-900 dark:text-white">
                                      {result.text}
                                    </span>
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {result.weight} 票 ({percent.toFixed(1)}%)
                                    </span>
                                  </div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                                    <div
                                      className="h-2.5 rounded-full"
                                      style={{
                                        width: `${percent}%`,
                                        backgroundColor: COLORS[index % COLORS.length],
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                          まだ投票がありません
                        </div>
                      )}
                    </div>

                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      <div>
                        最終更新:{' '}
                        {format(new Date(proposal.updatedAt), 'yyyy/MM/dd HH:mm:ss', {
                          locale: ja,
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* 投票者タブ */}
                {activeTab === 'voters' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                        投票者一覧
                      </h2>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        <button
                          onClick={exportAllVotersCSV}
                          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                          CSV
                        </button>
                      </div>
                    </div>
                    <VoterList proposalId={proposal.id} />
                  </div>
                )}

                {/* コメントタブ */}
                {activeTab === 'comments' && (
                  <div>
                    <Comments proposalId={proposal.id} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 削除確認モーダル */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowDeleteModal(false)}
          ></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              提案を削除しますか？
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              この操作は取り消せません。提案「{proposal.title}」を削除してよろしいですか？
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                キャンセル
              </button>
              <button
                onClick={handleDeleteProposal}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-800 flex items-center"
              >
                {isDeleting ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">削除中...</span>
                  </span>
                ) : (
                  <>
                    <TrashIcon className="h-5 w-5 mr-1" />
                    削除する
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalDetail;
